import { ReactElement, ReactNode, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { format } from 'date-fns';

import { Badge } from '@components/badge';
import { BadgeVariant } from '@components/badge/badge';
import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { createForm } from '@components/form';
import { FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconInfo } from '@components/icons/info';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconTraveller } from '@components/icons/traveller';
import { InfoBox } from '@components/info-box';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { useSidebar } from '@components/sidebar';
import { hideSidebar, Sidebar } from '@components/sidebar/sidebar';
import { Stack } from '@components/stack';
import { Stepper } from '@components/stepper';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';

import { AttributeType } from 'api/attributes/requests';
import {
  useBulkAddCompanyTravellerToJourney,
  useBulkAddCompanyTravellerToJourneyStats,
} from 'api/company-traveller/hooks';
import { BulkAddToJourneyStatsType } from 'api/company-traveller/requests';
import { useJourney, useJourneyAttributes } from 'api/journeys/hooks';
import { useActiveUsers } from 'api/users/hooks';
import { AttributeFields } from 'components/attribute-fields';
import { journeyAttributePrefix } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';

type TravellerJourneyAttributesType = {
  [K in `journey_attribute__${string}`]: string;
};
type TravellerJourneyAttributeArrayType = {
  journey_attribute_short_id: string;
  value: string | File | File[] | Date;
};

export const useBulkCompanyTravellerToJourneySidebar = () => {
  const sidebar = useSidebar(CompanyTravellerToJourney, {
    variant: 'LARGE',
    hasBackdrop: true,
  });
  return sidebar;
};

type SelectedTraveller = { companyTravellerShortId: string };
type CompanyTravellerToJourneyProps = {
  journeyShortId: string;
  selectedTravellers: SelectedTraveller[];
  onCancel?: () => void;
};

export const CompanyTravellerToJourney = ({
  journeyShortId,
  selectedTravellers,
  onCancel,
}: CompanyTravellerToJourneyProps) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const [step, setStep] = useState<'confirm' | 'configure'>('confirm');
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [startJourney, setStartJourney] = useState(false);
  const ids = selectedTravellers.map((traveller) => traveller.companyTravellerShortId);
  const journeyQuery = useJourney(journeyShortId);
  const selectedQuery = useBulkAddCompanyTravellerToJourneyStats({
    ids,
    journeyId: journeyShortId,
  });
  const selectedStatistics = selectedQuery.data;
  const attributeTemplatesQuery = useJourneyAttributes(journeyShortId);
  const journey = journeyQuery.data;
  const statistics = {
    numTravellers: journey?.numTotalTravellers,
    numNodeActions: journey?.numNodeActions,
  };
  const attributes = (attributeTemplatesQuery.data ?? []).filter((attribute) => attribute.isActive);

  const onSuccess = (data: { started: number }) => {
    showSuccess(t('travellersAdded', { count: data.started, title: journey?.title ?? '' }), {
      url: generatePath(routePaths.journeyDetails, {
        workspaceId: journey?.workspace ?? '',
        journeyId: journeyShortId,
        '*': null,
      }),
    });
    hideSidebar();
  };
  const bulkAddToJourneyMutation = useBulkAddCompanyTravellerToJourney({
    onSuccess,
  });

  const isSaving = bulkAddToJourneyMutation.isLoading;

  if (!journey || !selectedStatistics) {
    return <Spinner />;
  }

  const prepareAttributes = (attributes_data: TravellerJourneyAttributeArrayType[]) => {
    return attributes_data.reduce((acc, attribute) => {
      const attributeTemplate = attributes.find(
        (attr) => attr.shortId === attribute.journey_attribute_short_id
      );
      if (attributeTemplate?.attributeType === 'file_multi' && attribute.value instanceof Array) {
        attribute.value.forEach((file, idx) => {
          acc[`journey_attribute__${attribute.journey_attribute_short_id}__${idx}`] = file;
        });
      } else acc[`journey_attribute__${attribute.journey_attribute_short_id}`] = attribute.value;
      return acc;
    }, {} as Record<string, string | File | File[] | Date>);
  };

  const handleSave = async (attributes: TravellerJourneyAttributeArrayType[] = []) => {
    const data = {
      companyTravellers: ids.join(','),
      journeyId: journeyShortId,
      allowDuplicate,
      startJourney,
      ...prepareAttributes(attributes),
    };

    await bulkAddToJourneyMutation.mutateAsync(data);
  };
  const stepper = (
    <Stepper
      items={[
        { label: t('confirm') },
        { label: attributes.length > 0 ? t('configureAttributes') : t('noAttributes') },
      ]}
      activeIndex={step === 'confirm' ? 0 : 1}
      showCompleted
    />
  );

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('addToJourney')}
        icon={IconAddPlus}
        iconColor="elementBlue100"
        extraHeader={stepper}
      />
      <Sidebar.Content>
        {step === 'confirm' && (
          <Confirm
            title={journey.title}
            description={journey.description}
            selectedStatistics={selectedStatistics}
            journeyTravellers={statistics?.numTravellers ?? 0}
            journeyActions={statistics?.numNodeActions ?? 0}
            numAttributes={attributes.length}
            onContinue={() => setStep('configure')}
            onSave={handleSave}
            isSaving={isSaving}
            allowDuplicate={allowDuplicate}
            setAllowDuplicate={setAllowDuplicate}
            startJourney={startJourney}
            setStartJourney={setStartJourney}
            onCancel={onCancel}
          />
        )}
        {step === 'configure' && (
          <Configure attributes={attributes} onSave={handleSave} isSaving={isSaving} />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

const IconTextCount = ({
  icon: Icon,
  description,
  variant,
  children,
  dataTestid,
}: {
  icon: ReactElement<IconProps>;
  description: string;
  variant: BadgeVariant;
  children: ReactNode;
  dataTestid?: string;
}) => (
  <Stack orientation="horizontal" justify="space-between" data-testid={dataTestid}>
    <Stack orientation="horizontal" space={3}>
      {Icon}
      <Text font="bold14" text={description} />
    </Stack>
    <Badge text={String(children)} size="md" variant={variant} />
  </Stack>
);

type ConfirmType = {
  title: string;
  description?: string;
  selectedStatistics: BulkAddToJourneyStatsType;
  journeyTravellers: number;
  journeyActions: number;
  numAttributes: number;
  onContinue: () => void;
  onSave: () => void;
  isSaving: boolean;
  allowDuplicate: boolean;
  setAllowDuplicate: (allow: boolean) => void;
  startJourney: boolean;
  setStartJourney: (start: boolean) => void;
  onCancel?: () => void;
};

const Confirm = ({
  title,
  description,
  selectedStatistics,
  journeyTravellers,
  journeyActions,
  numAttributes,
  onContinue,
  onSave,
  isSaving,
  allowDuplicate,
  setAllowDuplicate,
  startJourney,
  setStartJourney,
  onCancel,
}: ConfirmType) => {
  const { t } = useScopedTranslate(locale);
  const hasAttributes = numAttributes > 0;
  const { totalTravellers, alreadyExists, notExists } = selectedStatistics;
  const toBeAdded = allowDuplicate ? totalTravellers : notExists;
  const isAllToBeAdded = alreadyExists === 0;

  const onCancelSidebar = () => {
    hideSidebar();
    if (onCancel) onCancel();
  };

  return (
    <>
      <Stack space={6}>
        <Stack space={4}>
          <IconTextCount
            icon={<IconTraveller color="elementPurpleInvert" />}
            description={t('travellersSelected')}
            children={totalTravellers}
            variant="neutral"
          />
          <Separator />
          <Stack space={2}>
            <IconTextCount
              dataTestid="not-in-journey"
              icon={<IconAddPlus color="elementBlue100" />}
              description={t('notInJourney')}
              children={notExists}
              variant="green"
            />
            <IconTextCount
              dataTestid="already-in-journey"
              icon={
                isAllToBeAdded ? (
                  <IconCheckmark color="elementPurple50" />
                ) : allowDuplicate ? (
                  <IconAddPlus color="elementBlue100" />
                ) : (
                  <IconCheckmark color="elementGreen100" />
                )
              }
              description={t('alreadyInJourney')}
              children={alreadyExists}
              variant={isAllToBeAdded ? 'neutral' : allowDuplicate ? 'green' : 'red'}
            />
          </Stack>
          {!isAllToBeAdded && (
            <Stack
              space={4}
              orientation="horizontal"
              align="center"
              justify="space-between"
              id="add-duplicate"
            >
              <Stack>
                <FormLabel text={t('addDuplicate')} />
                {allowDuplicate ? (
                  <Text text={t('core.yes')} font="regular14" />
                ) : (
                  <Text color="textHelper" text={t('core.no')} font="regular14" />
                )}
              </Stack>
              <Switch
                label={t('addDuplicate')}
                labelVisible={false}
                onChange={setAllowDuplicate}
                defaultValue={allowDuplicate}
              />
            </Stack>
          )}
          <Stack
            space={4}
            orientation="horizontal"
            align="center"
            justify="space-between"
            id="start-journey"
          >
            <Stack>
              <FormLabel text={t('startJourneyWhenAdded')} />
              {startJourney ? (
                <Text text={t('core.yes')} font="regular14" />
              ) : (
                <Text color="textHelper" text={t('core.no')} font="regular14" />
              )}
            </Stack>
            <Switch
              label={t('startJourneyWhenAdded')}
              labelVisible={false}
              onChange={setStartJourney}
              defaultValue={startJourney}
            />
          </Stack>
          <Card
            space={4}
            style={{ backgroundColor: theme.color.blueTrans5, paddingRight: theme.space[6] }}
          >
            <Stack orientation="horizontal" justify="space-between">
              <Stack orientation="horizontal" space={2} align="center">
                <IconAddPlus color="elementPurple60" />
                <Text font="bold14" text={t('toBeAdded')} />
              </Stack>
              <Text font="bold14" color="textSecondary" text={toBeAdded} />
            </Stack>
          </Card>
        </Stack>
        <Separator />
        <Stack space={4}>
          <ListCard
            icon={<IconJourneyItem />}
            title={title}
            description={description}
            actions={
              <ValueList
                items={[
                  { value: journeyTravellers, label: t('travellers') },
                  { value: journeyActions, label: t('actions') },
                ]}
              />
            }
          />
          {hasAttributes ? (
            <InfoBox
              text={t('hasAttributes', { count: numAttributes })}
              variant="indigo"
              icon={<IconInfo color="indigo100" />}
            />
          ) : (
            <Text font="regular14" text={t('noTravellerAttributes')} />
          )}
        </Stack>
      </Stack>
      <Sidebar.Footer>
        <ButtonGroup align="right">
          <Button variant="secondary" text={t('core.cancel')} onClick={onCancelSidebar} />
          {hasAttributes ? (
            <Button
              variant="primary"
              text={t('continue')}
              iconRight={<IconArrowRight />}
              onClick={onContinue}
              disabled={toBeAdded === 0}
            />
          ) : (
            <Button
              variant="primary-blue"
              text={t('addToJourney')}
              onClick={() => onSave()}
              disabled={isSaving || toBeAdded === 0}
            />
          )}
        </ButtonGroup>
      </Sidebar.Footer>
    </>
  );
};

const Form = createForm<TravellerJourneyAttributesType>();

const Configure = ({
  attributes,
  onSave,
  isSaving,
}: {
  attributes: AttributeType[];
  onSave: (data: TravellerJourneyAttributeArrayType[]) => void;
  isSaving: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const userQuery = useActiveUsers();
  const users = userQuery.data ?? [];
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);

  const onFileMultiRemove = (file: string) => {
    setFilesToRemove([...filesToRemove, file]);
  };

  const handleSave = (data: TravellerJourneyAttributesType) => {
    if (!data) return;
    const attributeData = Object.entries(data).map(([key, value = '']) => {
      const attributeKey = key.replace(journeyAttributePrefix, '');
      const attribute = attributes.find((attr) => attr.shortId === attributeKey);
      if (attribute?.attributeType === 'date') {
        value = value ? format(new Date(value), 'yyyy-MM-dd') : '';
      }
      return {
        journey_attribute_short_id: attributeKey,
        value,
      };
    });
    onSave(attributeData);
  };

  return (
    <Form onSubmit={handleSave}>
      <Stack space={4}>
        <IconTextCount
          icon={<IconAttributeJourney color="elementPurpleInvert" />}
          description={t('journeyAttributes')}
          children={attributes.length}
          variant="neutral"
        />
        <Text font="regular14" text={t('setDefaultAttributes')} />
        {attributes.map((attribute) => (
          <AttributeFields
            key={attribute.shortId}
            attributeTemplate={attribute}
            prefix={journeyAttributePrefix}
            label={attribute.title}
            usersData={users}
            onFileMultiRemove={onFileMultiRemove}
          />
        ))}
      </Stack>

      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
          <Form.Submit
            text={t('addToJourney')}
            variant="primary-blue"
            dataTestid="add-to-journey-button-sidebar"
            disabled={isSaving}
          />
        </Form.Actions>
      </Sidebar.Footer>
    </Form>
  );
};

const locale = {
  en: {
    addToJourney: 'Add to journey',
    confirm: 'Confirm',
    noAttributes: 'No fields to configure',
    configureAttributes: 'Configure fields',
    travellersSelected: 'Traveller/s Selected',
    noTravellerAttributes: 'This journey does not have any traveller journey fields',
    hasAttributes:
      'This journey has {count} Traveller journey fields you need to configure in the next step before adding',
    travellers: 'Travellers',
    actions: 'Actions',
    continue: 'Continue',
    journeyAttributes: 'Journey fields',
    setDefaultAttributes:
      'Set the default journey fields for selected Travellers being added to this journey',
    travellersAdded: '{count} added successfully to {title}',
    notInJourney: 'Not in journey',
    alreadyInJourney: 'Already in journey',
    addDuplicate: 'Add duplicate if traveller already exists',
    toBeAdded: 'To be added to journey',
    startJourneyWhenAdded: 'Start journey when added',
  },
};
